

import React, { useContext, useState, useRef, useMemo, useEffect } from 'react';
import _get from 'lodash.get';
import styled from 'styled-components';

import { useEditModeManager } from '../../../../../../../../../common/hooks';
import editModeManager from '../../../../../../../../../common/utils/EditModeManager';
import { SplashVideoDefaultPlaceholder, VideoPlayer } from '../../../../../../../../SplashRenderer';
import { Icon } from '../../../../../../../../../ui-kit';
import { ClickableDiv, RelativeBlock } from '../common/StyledEditables';
import { OptionsBlock } from '../../../Actions';
import { Edit, Visible, ThumbnailUrl as ThumbnailLink, Trash } from '../../../Actions/components/OptionsList';

import useOuterClick from '../../../../../../hooks/useOuterClick';

import { dataAdapter } from '../../../../../../contexts/dataAdapter';
import { theme } from '../../../../../../contexts/theme';

import getAssetType from '../../../../../../utils/getAssetType';

import { ActionTypes, ModuleType } from '../../../../../../types';

const inputElementPositions = {
  position: 'absolute',
  top: '40px',
  zIndex: '9'
};













const LoaderWrapper = styled.div`
  justify-content: center;
`;

const WithControlsPlayer = (props) => {
  const {
    video,
    handleTemplateChange,
    canUploadVideoImage,
    isVisible,
    isEditable,
    hideVisibleOption,
    moduleId,
    rowId,
    columnId
  } = props;

  const { source, thumbnailUrl, assetId } = video;

  const [canEdit, setShowEdit] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadedAssetId, setUploadedAssetId] = useState(assetId);
  const [isThumbnailLinkEditVisible, setThumbnailLinkEditVisible] = useState(false);
  const [inputData, setInputData] = useState(null);

  const componentId = useMemo(() => `video-${moduleId || 'unknown'}-${rowId || 'unknown'}-${columnId || 'unknown'}`, [moduleId, rowId, columnId]);

  const { primary } = useContext(theme);

  const actionsRef = useRef();
  const uploaderRef = useRef();

  const hideEdit = () => {
    editModeManager.clearActiveComponent();
    setShowEdit(false);
  };

  const showEdit = () => {
    editModeManager.setActiveComponent(componentId);
    setShowEdit(true);
  };

  const filePickerRefCallBack = (ref) => {
    uploaderRef.current = ref;
  };

  const showThumbnailLinkEdit = () => {
    setInputData({
      placeholder: 'Enter Thumbnail Link',
      isRequired: false,
      isLink: true,
      value: thumbnailUrl || '',
      style: { ...inputElementPositions, left: '40px', top: '60px' }
    });
    setThumbnailLinkEditVisible(true);
  };

  const hideThumbnailLinkEdit = () => {
    setThumbnailLinkEditVisible(false);
  };

  const {
    assetsMap,
    removeAsset
  } = useContext(dataAdapter);

  const handleOuterClick = () => {
    if (!canEdit) {
      return;
    }

    hideEdit();
    hideThumbnailLinkEdit();
  };

  useOuterClick({ dependencies: [canEdit], callback: handleOuterClick, innerRef: actionsRef });

  useEffect(() => {
    const asset = assetsMap.get(uploadedAssetId);
    if (asset && asset.loadedPercent === 100 && asset.uploadStatus !== 'active') {
      const assetType = getAssetType(asset.metaData);
      const descriptor = assetType === 'image' ? 'image' : 'video';

      // Delete asset from assetMap
      removeAsset(uploadedAssetId);

      handleTemplateChange(ActionTypes.UPDATE_BLOCK, {
        data: {
          moduleDescriptorValueToSet: {
            [descriptor]: {
              source: _get(asset, 'assetFiles.ORIGINAL.sourceUrl', ''),
              assetId: uploadedAssetId
            },
            ...(canUploadVideoImage && assetType === 'image' ? {
              style: { width: '100%' }
            } : {})
          },
          overrides: {
            type: assetType === 'image' ? ModuleType.IMAGE : ModuleType.VIDEO
          }
        }
      });
    }
  }, [uploadedAssetId, assetsMap, canUploadVideoImage]);

  useEditModeManager({
    componentId,
    callbackToHide: () => {
      setShowEdit(false);
      hideThumbnailLinkEdit();
    },
    optionalConditionToHide: canEdit || isThumbnailLinkEditVisible,
    dependencies: [canEdit, isThumbnailLinkEditVisible]
  });

  const handleBlockClick = (e) => {
    e.stopPropagation();

    if (isEditable) {
      showEdit();
    }

    return void 0;
  };

  const onChange = ({ isLoading: hasAssetUploaded, assetId: newAssetId }) => {
    setIsLoading(hasAssetUploaded);

    if (newAssetId) {
      setUploadedAssetId(newAssetId);
    }
  };


  const handleInputChanges = (value) => {
    handleTemplateChange(ActionTypes.UPDATE_BLOCK, {
      data: {
        moduleDescriptorValueToSet: {
          video: {
            ...video,
            thumbnailUrl: value
          }
        }
      }
    });
    hideThumbnailLinkEdit();
  };

  const onEdit = () => {
    if (uploaderRef.current) {
      uploaderRef.current.openFileChooser();
    }
    hideThumbnailLinkEdit();
  };

  const setIsVisible = () => {
    handleTemplateChange(ActionTypes.UPDATE_BLOCK, { data: {
        overrides: {
          isVisible: !isVisible
        }
      } });
    hideThumbnailLinkEdit();
  };

  const deleteAsset = () => {
    handleTemplateChange(ActionTypes.UPDATE_BLOCK, {
      data: {
        moduleDescriptorValueToSet: {
          video: {
            source: ''
          }
        },
        overrides: {
          type: ModuleType.VIDEO
        }
      }
    });
  };

  const options = [
  ...(!hideVisibleOption ? [
  {
    action: setIsVisible,
    label:
    <Visible isVisible={isVisible} />

  }] :
  []),
  {
    action: onEdit,
    label: <Edit />
  },
  ...(assetId ? [
  {
    action: deleteAsset,
    label:
    <Trash />

  }] : []),
  ...(source ? [
  {
    action: isThumbnailLinkEditVisible ? hideThumbnailLinkEdit : showThumbnailLinkEdit,
    isActive: isThumbnailLinkEditVisible || thumbnailUrl,
    label: <ThumbnailLink />
  }] : [])];




  if (isLoading) {
    return (
      <LoaderWrapper>
        <Icon name="shootstagrad" spin size={40} />
      </LoaderWrapper>);

  }

  return (
    <RelativeBlock>
      <ClickableDiv
        onClick={handleBlockClick}
        tabIndex="0"
        role="button"
        aria-pressed="false"
        onKeyDown={() => undefined}
        isVisible={isVisible}
        style={canEdit ? {
          boxShadow: '0 0 1px 4px #cbefe8',
          border: '3px solid #22bda3',
          borderRadius: '5px'
        } : {}}>

        {source ?
        <VideoPlayer
          videoUrl={source}
          thumbnailUrl={thumbnailUrl}
          brandingColor={primary} /> :


        <SplashVideoDefaultPlaceholder />}

      </ClickableDiv>
      {isEditable && canEdit &&
      <span ref={actionsRef}>
          <OptionsBlock
          options={options}
          acceptedFormats={canUploadVideoImage ? ['video/*', 'image/*'] : ['video/*']}
          filePickerRefCallBack={filePickerRefCallBack}
          onFileChange={onChange}
          showFileUploader
          isLinkEditVisible={isThumbnailLinkEditVisible}
          inputData={inputData}
          handleInputChanges={handleInputChanges} />

        </span>}

    </RelativeBlock>);

};

export default WithControlsPlayer;